import { getItemsMetadata } from "../services/catalogService";

const csvCell = (value) => {
    if (value === null || value === undefined) return "";
    const str = String(value);
    if (/[",\n]/.test(str)) return `"${str.replace(/"/g, '""')}"`;
    return str;
};

// summaryLines (optional): array of arrays, printed as a small block above
// the table, blank line, then headers/rows — this is how "upper" KPI/totals
// data rides along in the same file as the detailed table below it.
export const downloadCsv = (filename, headers, rows, summaryLines = []) => {
    const lines = [];
    if (summaryLines.length > 0) {
        summaryLines.forEach((line) => lines.push(line.map(csvCell).join(",")));
        lines.push("");
    }
    lines.push(headers.map(csvCell).join(","));
    rows.forEach((row) => lines.push(row.map(csvCell).join(",")));
    const csv = lines.join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
};

// Turns a { itemId: { costs: {name: value}, flags: [name], fields: {name: value} } }
// map into a consistent set of extra columns across the whole export, plus a
// getter for any given item's values in that column order.
const buildMetadataColumns = (metadataByItemId) => {
    const costNames = new Set();
    const fieldNames = new Set();
    let hasFlags = false;
    Object.values(metadataByItemId || {}).forEach((m) => {
        Object.keys(m.costs || {}).forEach((n) => costNames.add(n));
        Object.keys(m.fields || {}).forEach((n) => fieldNames.add(n));
        if ((m.flags || []).length > 0) hasFlags = true;
    });
    const costList = [...costNames];
    const fieldList = [...fieldNames];
    const extraHeaders = [
        ...costList.map((n) => `Cost: ${n}`),
        ...fieldList.map((n) => `Field: ${n}`),
        ...(hasFlags ? ["Flags"] : []),
    ];
    const getExtraValues = (itemId) => {
        const m = metadataByItemId[itemId] || { costs: {}, fields: {}, flags: [] };
        return [
            ...costList.map((n) => m.costs[n] ?? ""),
            ...fieldList.map((n) => m.fields[n] ?? ""),
            ...(hasFlags ? [(m.flags || []).join("; ")] : []),
        ];
    };
    return { extraHeaders, getExtraValues };
};

// Main entry point for SKU-level report exports: fetches costs/flags/custom
// fields for every distinct item in `rows`, then downloads a CSV with the
// report's own columns PLUS one column per distinct cost/field/flag found.
// Splits `ids` into batches and requests metadata for each batch separately
// — keeps every individual request small/fast regardless of how many
// distinct items a report export covers, and one slow/failed batch doesn't
// block the others (best-effort: a failed batch just contributes no
// metadata for its items, rather than failing the whole export).
const fetchMetadataInBatches = async (ids, batchSize = 150) => {
    const merged = {};
    for (let i = 0; i < ids.length; i += batchSize) {
        const batch = ids.slice(i, i + batchSize);
        try {
            const res = await getItemsMetadata(batch);
            Object.assign(merged, res.items || {});
        } catch {
            // This batch failed — leave its items without metadata rather
            // than aborting the whole export.
        }
    }
    return merged;
};

export const exportSkuReportCsv = async ({ filename, rows, headers, getRowValues, idKey, summaryLines = [] }) => {
    const ids = [...new Set(rows.map((r) => r[idKey]).filter(Boolean))];
    const metadataByItemId = ids.length > 0 ? await fetchMetadataInBatches(ids) : {};
    const { extraHeaders, getExtraValues } = buildMetadataColumns(metadataByItemId);
    const fullHeaders = [...headers, ...extraHeaders];
    const dataRows = rows.map((r) => [...getRowValues(r), ...getExtraValues(r[idKey])]);
    downloadCsv(filename, fullHeaders, dataRows, summaryLines);
};